// src/pages/misc/onboarding/StructureBuilderPage.tsx
// One-screen structure builder at /onboarding. Continues the landing.
// Questions change in place. The left bar grows from the bottom up.
//
// [MODULE-MANIFEST] See the big block in continuousPath.ts for routes, imports, storage and the Spanish->English URL table.
// [HANDOFF] THE SCREENS (ScreenId). Which ones show depends on the first answer (path):
//   S1  "¿Cuál eres?"  three-way slider (path 1 / 2 / 3). Skipped when the landing quiz already answered it (sessionStorage ventaje_s1_done).
//   SAC path 1 only ("Formo parte de una empresa"): "Crea tu cuenta y busca a tu empresa". Dead end by design: shows a note, creates nothing.
//   S2  company / organization name (required)
//   S3  how many companies (path 3 only)           stepper 1-20
//   S4  how many projects; path 3 may connect each project to a company (optional, "Omitir")   stepper 1-200
//   S5  how many sales teams                        stepper 1-10
//   S6  how many advisors; may assign each to a team (optional, "Omitir")   stepper 1-30
//   S7  who administers the account                 stepper 1-50 (admins see everything, they do not sell)
//   [PRICING] The stepper maxima are structureCeilings(book): Elite's limit where the price book states one, his where it does not (S-3);
//   BUILDER_CEILINGS while the book is unread. At a maximum the hint offers WhatsApp for more.
//   S8  summary tree "Así se ve <name>." then continue
//   path 1: S1, SAC   |   path 2: S1 S2 S4 S5 S6 S7 S8   |   path 3: S1..S8
// [HANDOFF] The three EXITS from S8 (see goNext): together (default) -> /onboarding/plan; mode="split" (/onboarding/previo) -> /signup?listo=1
//   [SIGNUP-SPLIT]; opened from the logged-in app with ?cuenta=1 -> /settings/create-organization/plan. All three first call markStructureDraftReady().
// [HANDOFF] Everything typed here lives in the `draft` state and is saved to localStorage (ventaje_draft_v3) on every change. Nothing is sent to a server.
// [PRICING] The "Estimado" badge (S6-S8) is the price book's range for the draft's seats (structureMoneyLabel(book, draft): the cheapest plan that fits to Elite, S-5); hidden while the book is unread. THE FAILED READ (S-9): one line with a retry in its place and the gold button held until a read succeeds. The real price is computed later in orderQuote.ts.
// [I18N] Every word on this page (questions, hints, buttons, choices) is typed Spanish in this file, not in the translation files.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './builder.css';
import { WA_PHONE } from '../public/wa';
import {
  BUILDER_CEILINGS,
  type LinkMap,
  type PathId,
  type StructureDraft,
  clampStructureDraft,
  isStructureDraftReady,
  loadStructureDraft,
  markStructureDraftReady,
  saveStructureDraft,
  structureCeilings,
  structureMoneyLabel,
} from './structureDraft';
import { BubbleRow, ObNode } from './treeBits';
import { PUBLIC_TAIL, rememberStructureLength } from './continuousPath';
import { PRICES_FAILED_LINE, usePriceBook } from './usePriceBook';

// [HANDOFF] sessionStorage flag set by LandingPathStep.tsx (the landing's last section) when the first question was already answered there.
export const SETUP_STEP_DONE = 'ventaje_s1_done';

type ScreenId = 'S1' | 'SAC' | 'S2' | 'S3' | 'S4' | 'S5' | 'S6' | 'S7' | 'S8';
type Draft = StructureDraft;
// The count each stepper screen edits (S3..S7); its ceiling is structureCeilings(book)'s, BUILDER_CEILINGS while the book is unread (S-3).
type CountKey = 'empresas' | 'proyectos' | 'equipos' | 'asesores' | 'admins';
const COUNT_KEYS: CountKey[] = ['empresas', 'proyectos', 'equipos', 'asesores', 'admins'];
const STEPPER_OF: Partial<Record<ScreenId, CountKey>> = { S3: 'empresas', S4: 'proyectos', S5: 'equipos', S6: 'asesores', S7: 'admins' };

// [ANIMATION] Slider knob position in % of the track for path 0..3 (0 = nothing chosen, parked at the far left edge).
// The three stop dots sit at 12 / 50 / 88 (see the .ob-stop map further down) but builder.css currently hides them (.ob-stop { display: none }).
// The fill bar and knob share these numbers.
// [VERIFY] POS, MINIS and CHOICE are duplicated in landing/LandingPathStep.tsx. Keep both copies identical.
const POS = [2, 12, 50, 88];

// [SPACING] The three illustrations (employee / own company / several companies). Each is a 90x80 viewBox, 1.6 stroke, color #A9E0F4.
// Rendered inside the .ob-mini buttons; the selected one gets the `on` class.
const MINIS = [
  '<svg viewBox="0 0 90 80" fill="none" stroke="#A9E0F4" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="45" cy="20" r="11"/><circle cx="45" cy="17" r="3.4"/><path d="M39.5 25c0-3 2.5-5 5.5-5s5.5 2 5.5 5"/><path d="M45 31v12"/><rect x="31" y="43" width="28" height="22" rx="4"/><path d="M38 65V52h14v13M42 50h1M48 50h1"/></svg>',
  '<svg viewBox="0 0 90 80" fill="none" stroke="#A9E0F4" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="32" y="8" width="26" height="22" rx="4"/><path d="M39 30V17h12v13M43 15h1M48 15h1"/><path d="M45 30v10M45 40H22v8M45 40h23v8M45 40v8"/><rect x="14" y="48" width="16" height="16" rx="4"/><rect x="37" y="48" width="16" height="16" rx="4"/><rect x="60" y="48" width="16" height="16" rx="4"/><circle cx="22" cy="56" r="2.2"/><circle cx="45" cy="56" r="2.2"/><circle cx="68" cy="56" r="2.2"/></svg>',
  '<svg viewBox="0 0 90 80" fill="none" stroke="#A9E0F4" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="36" y="4" width="18" height="15" rx="4"/><circle cx="45" cy="11.5" r="2.4"/><path d="M45 19v7M45 26H20v6M45 26h25v6M45 26v6"/><rect x="12" y="32" width="16" height="13" rx="4"/><rect x="37" y="32" width="16" height="13" rx="4"/><rect x="62" y="32" width="16" height="13" rx="4"/><path d="M20 45v6M45 45v6M70 45v6M14 51h12M39 51h12M64 51h12M14 51v5M26 51v5M39 51v5M51 51v5M64 51v5M76 51v5"/><g stroke-width="1.3"><rect x="9" y="56" width="10" height="9" rx="3"/><rect x="21" y="56" width="10" height="9" rx="3"/><rect x="34" y="56" width="10" height="9" rx="3"/><rect x="46" y="56" width="10" height="9" rx="3"/><rect x="59" y="56" width="10" height="9" rx="3"/><rect x="71" y="56" width="10" height="9" rx="3"/></g></svg>',
];

// [I18N] Text under the pictures for path 0..3 (index 0 = nothing chosen, empty). <b> = bold title, <em> = description,
// .ob-key = the slightly bolder role words (inmobiliaria, broker, desarrolladora...). Injected as HTML.
const CHOICE = [
  '',
  '<b>Formo parte de una empresa.</b><em>Vendo en el equipo de una <span class="ob-key">inmobiliaria</span>, un <span class="ob-key">broker</span> o una <span class="ob-key">desarrolladora</span>.</em>',
  '<b>Tengo mi empresa.</b><em>Soy <span class="ob-key">desarrollador</span> o <span class="ob-key">constructora</span>: manejo varios proyectos y mi propia empresa los vende.</em>',
  '<b>Administro varias empresas.</b><em>Soy <span class="ob-key">broker</span>, <span class="ob-key">master broker</span> o <span class="ob-key">inmobiliaria</span>, y vendo o administro la cuenta de varias desarrolladoras.</em>',
];

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

// The ordered list of screens for each path (see the table at the top of this file).
function stepsFor(path: PathId): ScreenId[] {
  if (path === 1) return ['S1', 'SAC'];
  if (path === 3) return ['S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8'];
  return ['S1', 'S2', 'S4', 'S5', 'S6', 'S7', 'S8'];
}

const StructureBuilderPage: React.FC<{ mode?: 'together' | 'split' }> = ({ mode = 'together' }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // [URL-RENAME] ?cuenta=1 -> ?account=1. true = opened from the logged-in app to create ANOTHER organization (no sign-up, no S1 skip).
  const fromAccount = searchParams.get('cuenta') === '1';
  // [HANDOFF] together = the live continuous onboarding (/onboarding). false = split version (/onboarding/previo) or logged-in.
  // Together adds the 4 tail screens (plan, extensiones, cuenta, checkout) to the progress count and changes the S8 button text.
  const together = mode === 'together' && !fromAccount;
  // [PRICING] The price book, one read per mount (usePriceBook): the stepper ceilings and the "Estimado" badge come from it (S-3, S-5).
  const { book, failed, retry } = usePriceBook();
  const ceilings = useMemo(() => (book === null ? null : structureCeilings(book)) ?? BUILDER_CEILINGS, [book]);
  // The draft is loaded from localStorage once, inside the ceilings in hand (S-16), then saved back on every change (effect below).
  const [draft, setDraft] = useState<Draft>(() => loadStructureDraft(ceilings));
  // When the book's ceilings arrive, a saved draft over one of them is brought inside (S-16); a draft already inside is kept as it is.
  useEffect(() => {
    setDraft((d) => {
      const inside = clampStructureDraft(d, ceilings);
      return COUNT_KEYS.every((key) => inside[key] === d[key]) ? d : inside;
    });
  }, [ceilings]);
  // True when the landing already answered S1: the builder then starts on S2 and "Atrás" on the first screen goes back to the landing quiz.
  const [skipFirst] = useState(() => {
    try {
      return !fromAccount && sessionStorage.getItem(SETUP_STEP_DONE) === '1' && loadStructureDraft().path !== 0;
    } catch {
      return false;
    }
  });
  // Current screen index inside `steps`. [URL-RENAME] ?resumen=1 -> ?summary=1: open directly on the last screen (S8). The plan page's "Atrás" uses it.
  const [idx, setIdx] = useState(() => {
    if (searchParams.get('resumen') !== '1') return 0;
    const saved = loadStructureDraft();
    const all = stepsFor(saved.path);
    let count = all.length;
    try {
      if (!fromAccount && sessionStorage.getItem(SETUP_STEP_DONE) === '1' && saved.path !== 0 && all[0] === 'S1') {
        count -= 1;
      }
    } catch { /* ignore */ }
    return Math.max(0, count - 1);
  });
  // "Connect mode" for the optional links. Tapping a child (project / advisor) selects it; tapping a parent (company / team) connects them.
  // map 'links' = project -> company (S4, path 3). map 'assign' = advisor -> team (S6). Tapping the empty page cancels it.
  const [connect, setConnect] = useState<{ map: 'links' | 'assign'; i: number } | null>(null);
  const [warnName, setWarnName] = useState(false);
  // Message shown in place of the hint (used by the SAC dead end).
  const [endNote, setEndNote] = useState('');
  // Size of the stage in px (the area that holds the tree). Measured by a ResizeObserver below. 900x520 is only the first-paint guess.
  const [box, setBox] = useState({ w: 900, h: 520 });
  const stageRef = useRef<HTMLDivElement>(null);
  const treesRef = useRef<HTMLDivElement>(null);
  const sumRef = useRef<HTMLDivElement>(null);
  const linesRef = useRef<SVGSVGElement>(null);
  const focusRef = useRef<HTMLDivElement | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const steps = useMemo(() => {
    const all = stepsFor(draft.path);
    if (skipFirst && draft.path !== 0 && all[0] === 'S1') return all.slice(1);
    return all;
  }, [draft.path, skipFirst]);
  const screen: ScreenId = steps[Math.min(idx, steps.length - 1)] ?? 'S1';
  // [ANIMATION] The thread/progress counts the builder screens plus the 4 tail screens (plan, extensiones, cuenta, checkout) in together mode.
  const tail = together && (draft.path === 2 || draft.path === 3) ? PUBLIC_TAIL.length : 0;
  const totalSteps = steps.length + tail;
  // [SPACING] Bubble grid math. "mobile" is decided from the STAGE width (not the window): under 820px.
  //   unit = bubble width + 18px gap = 86+18 (mobile) or 106+18 (desktop).
  //   cols = how many bubbles fit in min(900, stageWidth-48), clamped to 2..8. Each row's width is fixed to cols x unit - 18, so rows wrap exactly where expected.
  //   sumCols = columns per branch on the S8 summary: 3 on mobile, 4 on desktop.
  const mobile = box.w < 820;
  const unit = (mobile ? 86 : 106) + 18;
  const cols = clamp(Math.floor((Math.min(900, box.w - 48) + 18) / unit), 2, 8);
  const sumCols = mobile ? 3 : 4;

  // [HANDOFF] Autosave: the whole draft goes to localStorage (ventaje_draft_v3) after every change, savedAt stamped by the writer (S-16).
  useEffect(() => {
    saveStructureDraft(draft);
  }, [draft]);

  // Tell the later screens how many builder screens there were (sessionStorage), so their progress thread lines up.
  useEffect(() => {
    if (together) rememberStructureLength(steps.length);
  }, [together, steps.length]);

  // [SPACING] This page never scrolls. The body is locked (overflow hidden) while it is mounted and restored on leave.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // [ANIMATION] Re-measures the stage whenever it resizes; everything (cols, camera, lines) is recomputed from `box`.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The name field is focused automatically when S2 appears.
  useEffect(() => {
    if (screen === 'S2') nameRef.current?.focus();
  }, [screen]);

  // Merge a few fields into the draft and clear any end-note.
  const patch = useCallback((partial: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...partial }));
    setEndNote('');
  }, []);

  // Add or subtract one from a counter, always staying between its min and max.
  const bump = (key: 'empresas' | 'proyectos' | 'equipos' | 'asesores' | 'admins', dir: number, min: number, max: number) => {
    setDraft((d) => ({ ...d, [key]: clamp(d[key] + dir, min, max) }));
    setEndNote('');
  };

  // Camera: keep the row being asked about a little below the middle. Summary scales to fit.
  // [ANIMATION] CAMERA. The whole tree (`.ob-trees`) is one tall block that is moved with a CSS transform; the CSS transition on
  // .ob-trees (builder.css) makes it glide. Per screen:
  //   S8 summary  : scale = min(0.62, (stageHeight-24) / contentHeight), then vertically centered (never closer than 8px to the top).
  //   S7 admins   : scale = min(1, (stageHeight-24) / contentHeight), vertically centered.
  //   all others  : no scale (1). Move so the CENTER of the row being asked about (`focusRef`) sits at 56% of the stage height,
  //                 a little below the middle. With no focus row, center the whole tree.
  //   prefers-reduced-motion users get the same position without the explicit scale(1) step.
  // Re-runs whenever the screen, the draft, the stage size or the column count changes.
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const h = stage.clientHeight;
    if (screen === 'S8' && sumRef.current) {
      const el = sumRef.current;
      const sc = Math.min(0.62, (h - 24) / Math.max(el.offsetHeight, 1));
      const y = Math.max(8, (h - el.offsetHeight * sc) / 2);
      el.style.transform = `translateY(${y}px) scale(${sc})`;
    } else if (screen === 'S7' && treesRef.current) {
      const el = treesRef.current;
      const sc = Math.min(1, (h - 24) / Math.max(el.offsetHeight, 1));
      const y = Math.max(8, (h - el.offsetHeight * sc) / 2);
      el.style.transform = `translateY(${y}px) scale(${sc})`;
    } else if (treesRef.current) {
      const row = focusRef.current;
      const el = treesRef.current;
      let y = (h - el.offsetHeight) / 2;
      if (row) {
        const center = row.offsetTop + row.offsetHeight / 2;
        y = h * 0.56 - center;
      }
      el.style.transform = reduce ? `translateY(${y}px)` : `translateY(${y}px) scale(1)`;
    }
  }, [screen, draft, box, cols]);

  // White lines between levels. Redraw while the camera eases.
  // [ANIMATION] CONNECTING LINES. They are NOT part of the tree: one absolutely-placed <svg> (`.ob-lines`) covers the stage and its
  // <path>s are rebuilt from scratch on every run (svg.innerHTML = ''). Each path is a cubic curve from the bottom-center of a parent
  // node to the top-center of a child, with both control points at the vertical midpoint (a soft S-curve).
  //   stroke: rgba(255,255,255,.22) width 1.2 normally; rgba(255,255,255,.55) width 1.6 for lines drawn "strong" (the user-made links).
  //   Because the camera moves with a CSS transition, the lines are redrawn every 50ms and the timer stops after 700ms (about the transition length).
  //   Which lines: org -> companies (S3, S4 path 3); org -> projects (S4 path 2); company -> project by draft.links (S4 path 3);
  //   org -> teams (S5-S7); team -> advisor by draft.assign (S6, S7). S8 draws org -> branch label -> first row of each branch, then the same links.
  useLayoutEffect(() => {
    const svg = linesRef.current;
    const stage = stageRef.current;
    if (!svg || !stage) return;
    const draw = () => {
      svg.innerHTML = '';
      const ns = 'http://www.w3.org/2000/svg';
      const pt = (el: Element, side: 't' | 'b') => {
        const r = el.getBoundingClientRect();
        const s = stage.getBoundingClientRect();
        return { x: r.left - s.left + r.width / 2, y: side === 'b' ? r.bottom - s.top : r.top - s.top };
      };
      const seg = (x1: number, y1: number, x2: number, y2: number, strong = false) => {
        const p = document.createElementNS(ns, 'path');
        const my = (y1 + y2) / 2;
        p.setAttribute('d', `M${x1} ${y1} C${x1} ${my} ${x2} ${my} ${x2} ${y2}`);
        p.setAttribute('fill', 'none');
        p.setAttribute('stroke', strong ? 'rgba(255,255,255,.55)' : 'rgba(255,255,255,.22)');
        p.setAttribute('stroke-width', strong ? '1.6' : '1.2');
        svg.appendChild(p);
      };
      const nodes = (root: ParentNode | null) =>
        root ? Array.from(root.querySelectorAll('.ob-node')) : [];
      const linkKids = (parents: Element[], children: Element[], map: LinkMap, fromParentBottom: boolean) => {
        children.forEach((child, i) => {
          const pi = map[i];
          const parent = pi == null ? null : parents[pi];
          if (!parent) return;
          const a = pt(parent, 'b');
          const c = pt(child, 't');
          seg(a.x, a.y, c.x, c.y, !fromParentBottom);
        });
      };

      if (screen === 'S8') {
        const org = sumRef.current?.querySelector('.ob-node.org');
        if (!org) return;
        const ob = pt(org, 'b');
        sumRef.current?.querySelectorAll('.ob-blabel').forEach((label) => {
          const top = pt(label, 't');
          const bot = pt(label, 'b');
          seg(ob.x, ob.y, top.x, top.y);
          const branch = label.parentElement;
          const rows = branch ? Array.from(branch.querySelectorAll(':scope > .ob-row')) : [];
          const first = rows.find((r) => r.querySelector('.ob-node'));
          if (!first) return;
          nodes(first).forEach((n) => { const c = pt(n, 't'); seg(bot.x, bot.y, c.x, c.y); });
        });
        const emp = sumRef.current?.querySelector('[data-sum="emp"]');
        const pro = sumRef.current?.querySelector('[data-sum="pro"]');
        if (draft.path === 3 && emp && pro) linkKids(nodes(emp), nodes(pro), draft.links, false);
        const eq = sumRef.current?.querySelector('[data-sum="eq"]');
        const ase = sumRef.current?.querySelector('[data-sum="ase"]');
        if (eq && ase) linkKids(nodes(eq), nodes(ase), draft.assign, false);
        return;
      }

      const org = treesRef.current?.querySelector('.ob-tree:not([hidden]) .ob-node.org');
      if (!org) return;
      const o = pt(org, 'b');
      const row = (id: string) => treesRef.current?.querySelector(`[data-row="${id}"]`) ?? null;
      if (screen === 'S3' || (screen === 'S4' && draft.path === 3)) {
        nodes(row('emp')).forEach((n) => { const c = pt(n, 't'); seg(o.x, o.y, c.x, c.y); });
      }
      if (screen === 'S4') {
        if (draft.path === 2) nodes(row('pro')).forEach((n) => { const c = pt(n, 't'); seg(o.x, o.y, c.x, c.y); });
        else linkKids(nodes(row('emp')), nodes(row('pro')), draft.links, false);
      }
      if (screen === 'S5' || screen === 'S6' || screen === 'S7') {
        nodes(row('eq')).forEach((n) => { const c = pt(n, 't'); seg(o.x, o.y, c.x, c.y); });
      }
      if (screen === 'S6' || screen === 'S7') linkKids(nodes(row('eq')), nodes(row('ase')), draft.assign, false);
    };
    draw();
    const id = window.setInterval(draw, 50);
    const stop = window.setTimeout(() => window.clearInterval(id), 700);
    return () => { window.clearInterval(id); window.clearTimeout(stop); };
  }, [screen, draft, box, cols, connect]);

  // [HANDOFF] "Continuar" for every screen. Validation: S1 needs a choice, S2 needs a name (the field gets the `warn` class and focus).
  // SAC never advances (it only shows a note). S8 is the exit: mark the draft ready, then go to the next stage (see the top of this file).
  const goNext = () => {
    if (screen === 'S1') {
      if (draft.path === 0) return;
      setIdx(1);
      return;
    }
    if (screen === 'S2' && !draft.name.trim()) {
      setWarnName(true);
      nameRef.current?.focus();
      return;
    }
    // [HANDOFF] SAC stays a dead end (S-13): the employee flow, joining an existing company, is owed to the designer's unpushed /signup; nothing is created here.
    if (screen === 'SAC') {
      setEndNote(
        fromAccount
          ? 'Ya tienes cuenta. Para crear una organización, elige “Mi empresa” o “Varias empresas”.'
          : 'Aquí termina esta parte. La cuenta llega en el siguiente paso.',
      );
      return;
    }
    if (screen === 'S8') {
      markStructureDraftReady();
      // [HANDOFF] Three exits. Logged-in app -> its own plan page. TOGETHER -> /onboarding/plan (then extensiones, cuenta, checkout).
      // SPLIT -> /signup?listo=1 = [SIGNUP-SPLIT], the only exit that reaches a real sign-up right away. [URL-RENAME] listo -> ready.
      if (fromAccount) navigate('/settings/create-organization/plan');
      else if (together) navigate('/onboarding/plan');
      else navigate('/signup?listo=1');
      return;
    }
    setConnect(null);
    setIdx((i) => Math.min(i + 1, steps.length - 1));
  };

  const nodeClass = (map: 'links' | 'assign', index: number, isParent: boolean) => {
    if (!connect || connect.map !== map) return '';
    if (!isParent && connect.i === index) return 'sel';
    if (!isParent) return 'faded';
    return 'target';
  };

  const onChild = (map: 'links' | 'assign', index: number) => {
    setConnect((c) => (c && c.map === map && c.i === index ? null : { map, i: index }));
  };
  const onParent = (map: 'links' | 'assign', parentIndex: number) => {
    if (!connect || connect.map !== map) return;
    const key = map;
    setDraft((d) => ({ ...d, [key]: { ...d[key], [connect.i]: parentIndex } }));
    setConnect(null);
  };

  // [PRICING] From S6 on the screen prices the draft: the badge from the book, or THE FAILED READ's line with the gold button held (S-9).
  const priced = ['S6', 'S7', 'S8'].includes(screen);
  const estimate = book === null ? null : structureMoneyLabel(book, draft);
  const showEstimate = priced && draft.asesores + draft.admins > 0 && estimate !== null;
  const orgLabel = draft.name || (draft.path === 3 ? 'Tu organización' : 'Tu empresa');
  const fill = totalSteps <= 1 ? 0 : (Math.min(idx, steps.length - 1) / (totalSteps - 1)) * 100;
  const adminRows = clamp(Math.floor((box.h - 40) / 102), 2, 6);

  // [ANIMATION] PRESS-AND-HOLD on the stepper buttons. A normal click adds/subtracts 1 (onClick). If the pointer stays down for
  // 380ms, it then repeats every 80ms until the pointer is released or cancelled. The release listeners are removed again each time.
  const hold = (dir: number, key: 'empresas' | 'proyectos' | 'equipos' | 'asesores' | 'admins', min: number, max: number) => {
    const start = window.setTimeout(() => {
      const repeat = window.setInterval(() => bump(key, dir, min, max), 80);
      const up = () => { window.clearInterval(repeat); window.removeEventListener('pointerup', up); };
      window.addEventListener('pointerup', up);
    }, 380);
    const cancel = () => window.clearTimeout(start);
    window.addEventListener('pointerup', cancel, { once: true });
    window.addEventListener('pointercancel', cancel, { once: true });
  };

  // The big "- 12 +" control. The number in the middle is also an input: only digits are kept and the value is clamped to min..max.
  const Stepper = (key: 'empresas' | 'proyectos' | 'equipos' | 'asesores' | 'admins', min: number, max: number) => (
    <div className="ob-stepper">
      <button type="button" className="ob-sbtn" onClick={() => bump(key, -1, min, max)} onPointerDown={() => hold(-1, key, min, max)}>−</button>
      <input
        className="ob-sval"
        inputMode="numeric"
        value={draft[key]}
        onChange={(e) => {
          const v = parseInt(e.target.value.replace(/\D/g, ''), 10);
          if (!Number.isNaN(v)) setDraft((d) => ({ ...d, [key]: clamp(v, min, max) }));
        }}
      />
      <button type="button" className="ob-sbtn" onClick={() => bump(key, 1, min, max)} onPointerDown={() => hold(1, key, min, max)}>+</button>
    </div>
  );

  // [I18N] The question, per screen. <b> parts are bold (the rest is the light weight, see .ob-q in builder.css).
  const question = () => {
    if (screen === 'S1') return <>¿Cuál <b>eres?</b></>;
    if (screen === 'SAC') return <>Crea tu cuenta y <b>busca a tu empresa.</b></>;
    if (screen === 'S2') return draft.path === 3 ? <>¿Cómo se llama tu <b>organización?</b></> : <>¿Cómo se llama tu <b>empresa?</b></>;
    if (screen === 'S3') return <>¿Cuántas <b>empresas</b> administras?</>;
    if (screen === 'S4') return draft.path === 3
      ? <>¿Cuántos <b>proyectos</b> tienen, entre todas tus empresas?</>
      : <>¿Cuántos <b>proyectos</b> manejas?</>;
    if (screen === 'S5') return <>¿Cuántos <b>equipos de ventas</b> tienen?</>;
    if (screen === 'S6') return <>¿Cuántos <b>asesores</b> tienen en total?</>;
    if (screen === 'S7') return <>¿Quiénes <b>administran</b> la cuenta?</>;
    return <>Así se ve <b>{draft.name || 'tu empresa'}.</b></>;
  };

  // [I18N] The small grey line under the question (12px, label color). An endNote, when present, replaces it.
  const hint = () => {
    if (endNote) return endNote;
    // [PRICING] At a stepper's maximum (S-3) the hint offers WhatsApp for more: the whole line is the link.
    const counted = STEPPER_OF[screen];
    if (counted && draft[counted] >= ceilings[counted]) {
      return (
        <a className="underline" href={`https://wa.me/${WA_PHONE}`} target="_blank" rel="noopener noreferrer">
          ¿Más de {ceilings[counted]}? Escríbenos por WhatsApp.
        </a>
      );
    }
    if (screen === 'S1') return 'Desliza para ver →';
    if (screen === 'S4' && draft.path === 3) return 'Toca un proyecto para conectarlo a su empresa (opcional).';
    if (screen === 'S5') return 'Ej. internos y externos. Puedes renombrarlos.';
    if (screen === 'S6') return 'Toca un asesor para asignarlo a su equipo (opcional).';
    if (screen === 'S7') return 'Dueños, gerentes, líderes de equipo, o incluso un cliente que sólo quiere ver su cuenta. Ven todo, no venden.';
    if (screen === 'S8') return 'Guarda tu estructura y ve tu plan exacto.';
    if (screen === 'SAC') return 'La cuenta nueva, o entrar, llega en el siguiente paso.';
    return '';
  };

  const showCompany = screen === 'S2' || screen === 'S3' || screen === 'S4';
  const showSales = screen === 'S5' || screen === 'S6' || screen === 'S7';

  return (
    <>
      {/* [SPACING] LAYOUT (all in builder.css). .ob is a fixed full-screen column with a 64px left rail (46px under 820px) that the
          thread sits in. Top to bottom: .ob-top (logo + "Paso X de Y"), .ob-stage (flex 58%, holds the tree/lines/S1), .ob-panel
          (question card, max 640px, then the back/continue row, then the estimate). The panel keeps 6vh of space under it. */}
      {/* [ANIMATION] .ob-vignette = dark gradients on the left and right 46% of the screen so the background fades out behind the content. */}
      <div className="ob-vignette" />
      {/* [ANIMATION] LEFT THREAD: a 3px line at left 30px (16px on mobile). The fill grows from the BOTTOM up, height = progress %,
          with a 0.6s ease (cubic-bezier .22,1,.36,1). It is the landing's thread flipped. The white cap on its top end is ::after (5x28px). */}
      <div className="ob-thread" aria-hidden>
        <div className="ob-thread-track" />
        <div className="ob-thread-fill" style={{ height: `${fill}%` }} />
      </div>
      <div className="ob" onClick={() => connect && setConnect(null)}>
        <div className="ob-top">
          <span className="ob-logo">Ventaje</span>
          <span className="ob-tag">{draft.path === 0 ? 'Paso 1' : `Paso ${Math.min(idx, steps.length - 1) + 1} de ${totalSteps}`}</span>
        </div>

        {/* [ANIMATION] STAGE. Layers (z-index): lines svg 1, tree 2, S1 pictures 3. Everything inside is clipped (overflow hidden). */}
        <div className="ob-stage" ref={stageRef}>
          <svg className="ob-lines" ref={linesRef} />
          {/* [ANIMATION] TREE. .ob-trees is a flex row of up to two trees (company side, sales side) with a 48px gap (34px mobile),
              each a column with 104px between levels (--ob-gap, 78px mobile). The nodes pop in with the ob-pop animation (0.25s, scale 0 -> 1.08 -> 1).
              The camera moves this whole block, see the camera effect above. */}
          {screen !== 'S1' && screen !== 'SAC' && screen !== 'S8' && (
            <div className="ob-trees" ref={treesRef}>
              {showCompany && (
                <div className="ob-tree">
                  <div className="ob-row" ref={screen === 'S2' ? focusRef : undefined}>
                    <ObNode kind="org" name={orgLabel} org empty={!draft.name} />
                  </div>
                  {screen === 'S3' || (screen === 'S4' && draft.path === 3) ? (
                    <div data-row="emp" ref={screen === 'S3' ? focusRef : undefined}>
                      <BubbleRow unit={unit} kind="empresa" count={draft.empresas} label="Empresa" cols={cols} nodeClass={(i) => nodeClass('links', i, true)} onNode={screen === 'S4' ? (i) => onParent('links', i) : undefined} />
                    </div>
                  ) : null}
                  {screen === 'S4' && (
                    <div data-row="pro" ref={focusRef}>
                      <BubbleRow unit={unit} kind="proyecto" count={draft.proyectos} label="Proyecto" cols={cols} nodeClass={(i) => (draft.path === 3 ? nodeClass('links', i, false) : '')} onNode={draft.path === 3 ? (i) => onChild('links', i) : undefined} />
                    </div>
                  )}
                </div>
              )}
              {showSales && (
                <div className="ob-tree">
                  <div className="ob-row" ref={screen === 'S5' ? focusRef : undefined}>
                    <ObNode kind="org" name={orgLabel} org empty={!draft.name} />
                  </div>
                  <div data-row="eq" ref={screen === 'S5' ? focusRef : undefined}>
                    <BubbleRow unit={unit} kind="equipo" count={draft.equipos} label="Equipo" cols={cols} nodeClass={(i) => nodeClass('assign', i, true)} onNode={screen === 'S6' ? (i) => onParent('assign', i) : undefined} />
                  </div>
                  {(screen === 'S6' || screen === 'S7') && (
                    <div data-row="ase" ref={screen === 'S6' ? focusRef : undefined}>
                      <BubbleRow unit={unit} kind="asesor" count={draft.asesores} label="Asesor" cols={cols} nodeClass={(i) => nodeClass('assign', i, false)} onNode={screen === 'S6' ? (i) => onChild('assign', i) : undefined} />
                    </div>
                  )}
                </div>
              )}
              {/* [SPACING] ADMINS (S7): a column-first grid of 3 columns, `adminRows` rows tall (fits the stage height: floor((h-40)/102), 2..6).
                  When there are more admins than 3 x rows, the last slot becomes a "+N" bubble. */}
              {screen === 'S7' && (
                <div className="ob-admins" style={{ gridTemplateRows: `repeat(${adminRows}, auto)` }}>
                  {Array.from({ length: Math.min(draft.admins, adminRows * 3) === draft.admins ? draft.admins : adminRows * 3 - 1 }, (_, i) => (
                    <ObNode key={i} kind="admin" name={`Admin ${i + 1}`} />
                  ))}
                  {draft.admins > adminRows * 3 && <div className="ob-more">+{draft.admins - (adminRows * 3 - 1)}</div>}
                </div>
              )}
            </div>
          )}

          {/* [ANIMATION] SUMMARY (S8). Org at the top, then two branches 120px apart: "Tu empresa / Tu estructura" (companies, projects)
              and "Tus ventas" (teams, advisors), then one row of admins. Each BubbleRow shows at most cols x rows bubbles (companies 2 rows,
              projects 3, teams 2, advisors 3, admins 1) and a "+N" bubble for the rest. The whole block is scaled down to fit (max 0.62). */}
          {screen === 'S8' && (
            <div className="ob-sum" ref={sumRef}>
              <div className="ob-row"><ObNode kind="org" name={orgLabel} org empty={!draft.name} /></div>
              <div className="ob-branches">
                <div className="ob-branch">
                  <span className="ob-blabel">{draft.path === 3 ? 'Tu estructura' : 'Tu empresa'}</span>
                  {draft.path === 3 && (
                    <div data-sum="emp"><BubbleRow unit={unit} kind="empresa" count={draft.empresas} label="Empresa" cols={sumCols} rows={2} /></div>
                  )}
                  <div data-sum="pro"><BubbleRow unit={unit} kind="proyecto" count={draft.proyectos} label="Proyecto" cols={sumCols} rows={3} /></div>
                </div>
                <div className="ob-branch">
                  <span className="ob-blabel">Tus ventas</span>
                  <div data-sum="eq"><BubbleRow unit={unit} kind="equipo" count={draft.equipos} label="Equipo" cols={sumCols} rows={2} /></div>
                  <div data-sum="ase"><BubbleRow unit={unit} kind="asesor" count={draft.asesores} label="Asesor" cols={sumCols} rows={3} /></div>
                </div>
              </div>
              <BubbleRow unit={unit} kind="admin" count={draft.admins} label="Admin" cols={sumCols * 2 + 1} rows={1} />
            </div>
          )}

          {/* [SPACING] S1 PICTURES: three buttons 198x184px (106x122 on mobile), 36px apart (12 on mobile). Unselected = 38% opacity, scale .9;
              selected = full opacity, scale 1.08 with a soft white glow; 0.4s transition. The description sits at 72% of the stage height (70% mobile). */}
          {screen === 'S1' && (
            <div className="ob-s1">
              <div className="ob-minis">
                {MINIS.map((svg, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`ob-mini${draft.path === i + 1 ? ' on' : ''}`}
                    aria-pressed={draft.path === i + 1}
                    onClick={(e) => { e.stopPropagation(); patch({ path: (i + 1) as PathId }); setIdx(0); }}
                    dangerouslySetInnerHTML={{ __html: svg }}
                  />
                ))}
              </div>
              <div className="ob-choice" dangerouslySetInnerHTML={{ __html: CHOICE[draft.path] }} />
            </div>
          )}
        </div>

        <div className="ob-panel">
          <div className="ob-card">
            <h1 className="ob-q">{question()}</h1>
            <p className="ob-hint">{hint()}</p>
            <div className="ob-ctrl">
              {/* [ANIMATION] SLIDER (S1): 10px track; the fill (gradient light blue -> white) and the 22px white knob move together with a
                  0.3s ease to POS[path]. An invisible native range input sits on top (inset -16px) so touch/drag/keyboard all work.
                  Choosing with the pictures or the slider is the same state (draft.path) and returns to the first screen. */}
              {screen === 'S1' && (
                <div className="ob-slider">
                  <div className="ob-track">
                    <div className="ob-sfill" style={{ width: `${POS[draft.path]}%` }} />
                    {[12, 50, 88].map((left, i) => (
                      <span key={left} className={`ob-stop${draft.path >= i + 1 ? ' on' : ''}`} style={{ left: `${left}%` }} />
                    ))}
                    <div className="ob-knob" style={{ left: `${POS[draft.path]}%` }} />
                    <input
                      className="ob-srange"
                      type="range"
                      min={0}
                      max={3}
                      step={1}
                      value={draft.path}
                      aria-label="Qué tipo de operación tienes"
                      onChange={(e) => { patch({ path: Number(e.target.value) as PathId }); setIdx(0); }}
                    />
                  </div>
                  <div className="ob-slabels"><span>Formo parte</span><span>Mi empresa</span><span>Varias empresas</span></div>
                </div>
              )}
              {screen === 'S2' && (
                <div className="ob-field">
                  <input
                    ref={nameRef}
                    className={warnName && !draft.name ? 'warn' : ''}
                    placeholder="Ej. Grupo Inmobiliario Sol"
                    value={draft.name}
                    onChange={(e) => { setWarnName(false); patch({ name: e.target.value }); }}
                  />
                </div>
              )}
              {screen === 'S3' && Stepper('empresas', 1, ceilings.empresas)}
              {screen === 'S4' && Stepper('proyectos', 1, ceilings.proyectos)}
              {screen === 'S5' && Stepper('equipos', 1, ceilings.equipos)}
              {screen === 'S6' && Stepper('asesores', 1, ceilings.asesores)}
              {screen === 'S7' && Stepper('admins', 1, ceilings.admins)}
              {screen === 'S8' && (
                <div className="ob-sumline">
                  {draft.path === 3 && <span><b>{draft.empresas}</b> empresas</span>}
                  <span><b>{draft.proyectos}</b> proyectos</span>
                  <span><b>{draft.equipos}</b> equipos</span>
                  <span><b>{draft.asesores}</b> asesores</span>
                  <span><b>{draft.admins}</b> administradores</span>
                </div>
              )}
            </div>
          </div>
          {/* [SPACING] FOOT ROW: max 640px, 18px below the card. "Atrás" is hidden (kept in layout) on the first screen. "Omitir" appears only
              on S4 (path 3) and S6. The gold button text: S1+path 1 "Crear cuenta o entrar" (SAC), S8 "Continuar a planes" (together) or
              "Guardar y continuar" (split / logged-in), otherwise "Continuar". */}
          <div className="ob-foot">
            <button type="button" className={`ob-back${idx === 0 && !skipFirst && !fromAccount ? ' is-off' : ''}`} onClick={(e) => {
              e.stopPropagation();
              setConnect(null);
              setEndNote('');
              if (idx === 0) {
                if (fromAccount) navigate(isStructureDraftReady() ? '/settings/create-organization' : '/dashboard');
                else if (skipFirst) navigate('/landing#cuestionario');
                return;
              }
              setIdx((i) => Math.max(0, Math.min(i, steps.length - 1) - 1));
            }}>
              Atrás
            </button>
            <div className="ob-foot-right">
              {(screen === 'S4' && draft.path === 3) || screen === 'S6' ? (
                <button type="button" className="ob-skip" onClick={(e) => { e.stopPropagation(); setConnect(null); goNext(); }}>Omitir</button>
              ) : null}
              {screen === 'S1' && draft.path === 0 ? null : (
                <button
                  type="button"
                  className="btn-gold inline-flex items-center justify-center min-h-[52px] px-7 rounded-[10px] text-[12px] font-bold uppercase tracking-[0.1em] disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={priced && failed}
                  onClick={(e) => { e.stopPropagation(); goNext(); }}
                >
                  {screen === 'S1' && draft.path === 1 && !fromAccount ? 'Crear cuenta o entrar →' : screen === 'S8' ? (together ? 'Continuar a planes →' : 'Guardar y continuar →') : 'Continuar'}
                </button>
              )}
            </div>
          </div>
          {/* [PRICING] "Estimado" badge: only from S6 on, only when advisors + admins > 0 and only once the book priced the draft (structureMoneyLabel). */}
          {showEstimate && (
            <div className="ob-est">
              <span className="ob-tag">Estimado</span>
              <span className="v">{estimate}</span>
              <span className="ob-tag">al mes</span>
            </div>
          )}
          {/* [PRICING] THE FAILED READ (S-9): from S6 on, the one line in place of the badge; it is the retry. The gold button waits for a read. */}
          {priced && failed && (
            <div className="ob-est">
              <button type="button" className="ob-tag underline" onClick={retry}>{PRICES_FAILED_LINE}</button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default StructureBuilderPage;
